import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Box, Typography, Button } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import VerifyMobileNumber from "./VerifyMobileNumber";
import { getOTP, userLogin, verifyOTP } from "../../redux/actions/appActions";
import {
  ROUTE_CRAFTING_VIDEO,
  ROUTE_EDUCATION_NEW,
  ROUTE_EXPERIENCE_NEW,
  ROUTE_PROFILER,
  ROUTE_WELCOME_NEW,
  ROUTE_WHAT_YOUR_NAME,
} from "../../constants/routes";
import { splitInternationalDigits, toInternationalDigits } from "../../utils/phone";

type FormValues = Yup.InferType<any>;

const schema = Yup.object().shape({
  phoneNumber: Yup.string()
    .required("Phone number is required")
    .test("valid-tel", "Enter a valid phone number", (val) => (val ? matchIsValidTel(val) : false)),
  otp: Yup.string().notRequired(),
});

const SignInNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [otpSent, setOtpSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const defaultValues = {
    phoneNumber: "",
    otp: "",
  };

  const { handleSubmit, control, watch } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  const otp = watch("otp");

  const onOTPSent = () => {
    setOtpSent(true);
  };

  const handleSendOtp = (data: any) => {
    const digits = toInternationalDigits(String(data?.phoneNumber ?? ""));
    if (!digits) {
      return;
    }
    const split = splitInternationalDigits(digits);
    if (!split) {
      return;
    }
    setPhoneNumber(digits);
    dispatch(getOTP({ nationalNumber: split.nationalNumber, countryCallingCode: split.countryCallingCode }, onOTPSent));
  };

  const onOTPVerified = (otpValue: string) => {
    const split = splitInternationalDigits(phoneNumber);
    if (!split) {
      return;
    }
    const payload = {
      userName: split.nationalNumber,
      countryCode: split.countryCallingCode,
      otp: otpValue,
    };

    dispatch(
      userLogin(payload, (res: any) => {
        const status = res?.profileStatus;
        if (status?.profileStatus === "INACTIVE") {
          navigate(ROUTE_WHAT_YOUR_NAME, { replace: true });
        } else if (status?.educationStatus === "INACTIVE") {
          navigate(ROUTE_EDUCATION_NEW, { replace: true });
        } else if (status?.experienceStatus === "INACTIVE") {
          navigate(ROUTE_EXPERIENCE_NEW, { replace: true });
        } else if (status?.examStatus === "INACTIVE") {
          navigate(ROUTE_PROFILER, { replace: true });
        } else if (status?.videoStatus === "INACTIVE") {
          navigate(ROUTE_CRAFTING_VIDEO, { replace: true });
        } else {
          navigate(ROUTE_WELCOME_NEW);
        }
      })
    );
  };

  const handleVerifyOtp = (data: any) => {
    const split = splitInternationalDigits(phoneNumber);
    if (!split) {
      return;
    }
    dispatch(
      verifyOTP(
        {
          phoneNumber: split.nationalNumber,
          countryCode: split.countryCallingCode,
          otp: data?.otp,
        },
        () => onOTPVerified(data?.otp)
      )
    );
  };

  if (otpSent) {
    return (
      <VerifyMobileNumber
        mobileNumber={phoneNumber}
        control={control}
        onSubmit={handleVerifyOtp}
        handleSubmit={handleSubmit}
        otp={otp}
        resendCodeDuration={30}
        handleSendOtp={handleSendOtp}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingTop: "10px",
          fontSize: "18px",
          color: "white",
          backgroundColor: "white",
          maxWidth: "410px",
          margin: "auto",
        }}
      >
        <Box
          component="img"
          src="https://cdn.builder.io/api/v1/image/assets/TEMP/2e88e2fbec7ace948fcde95ec056e8b760fc06d3dfba3d5d3f7fa80729a3c10b?apiKey=80f439f9d23243918de9eba558cc207a&"
          alt=""
          sx={{
            alignSelf: "center",
            aspectRatio: "1.92",
            width: "94px",
          }}
        />
        <Box
          component="form"
          onSubmit={handleSubmit(handleSendOtp)}
          className="bg-white roundedten pad-desktop-signup-new"
          sx={{
            padding: "40px",
            "@media (max-width:600px)": {
              padding: "20px",
            },
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            align="center"
            sx={{
              marginTop: "16px",
              fontWeight: 500,
              lineHeight: "36px",
              fontSize: "25px",
            }}
          >
            Welcome to SeeVi!
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{
              marginTop: "20px",
              lineHeight: "24px",
              marginBottom: "20px",
            }}
          >
            A unique job match making platform
          </Typography>
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field: { value, onChange, onBlur, name, ref }, fieldState: { error } }) => (
              <MuiTelInput
                inputRef={ref}
                name={name}
                value={value}
                onChange={(v) => onChange(v)}
                onBlur={onBlur}
                defaultCountry="IN"
                preferredCountries={["IN"]}
                error={!!error}
                helperText={error?.message || " "}
                variant="outlined"
                fullWidth
                placeholder="Phone number"
                sx={{
                  marginTop: "20px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "95px",
                    backgroundColor: "rgba(63, 81, 181, 0.3)",
                    color: "white",
                    "& fieldset": {
                      border: "none",
                    },
                    "& input": {
                      padding: "14px 14px 14px 8px",
                      fontWeight: 500,
                      color: "white",
                    },
                  },
                  "& .MuiTelInput-IconButton": {
                    color: "white",
                  },
                }}
              />
            )}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              justifyContent: "center",
              alignItems: "center",
              padding: "15px 64px",
              marginTop: "200px",
              marginBottom: "16px",
              fontSize: "16px",
              fontWeight: 600,
              color: "#3f51b5",
              backgroundColor: "white",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "95px",
              textTransform: "none",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.9)",
              },
            }}
          >
            Next Up: Verification
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SignInNew;
